import type { TAttributesChangesMap } from "types/directoryAttribute";
import type {
  DirectoryConfigurationStatus,
  DirectorySyncMode,
  TConfigurationCreateBody,
  TConfigurationUpdateBody,
  TDirectory,
} from "types/directoryConfiguration";

import { List, Map } from "immutable";
import { camelCase, chain, isArray, isObject, map } from "lodash";
import { DirectoryConfigurationModel } from "models/DirectoryConfigurationModel";
import { apiReq } from "utils/api/apiReq";
import { removeRedundantSpaces } from "utils/strings";

import type { ICheckConfig } from "./company";

import { getAsQueryParams } from "./utils";

export const getDirectoryConfigurations = async (): Promise<List<DirectoryConfigurationModel>> => {
  const { data } = await apiReq("GET", "/v1/directoryConfigurations");

  return List<DirectoryConfigurationModel>(
    data.map((item: unknown) => DirectoryConfigurationModel.fromServerData(item)),
  );
};
export const createDirectoryConfiguration = async (
  creationData: TConfigurationCreateBody,
  options: { triggerSync?: boolean } = {},
): Promise<DirectoryConfigurationModel> => {
  const { data } = await apiReq("POST", `/v1/directoryConfigurations?${getAsQueryParams(options)}`, creationData);
  return DirectoryConfigurationModel.fromServerData(data);
};

export const editDirectoryConfiguration = async (
  id: string,
  body: TConfigurationUpdateBody,
): Promise<DirectoryConfigurationModel> => {
  const { data } = await apiReq("PUT", `/v1/directoryConfigurations/${id}`, body);

  return DirectoryConfigurationModel.fromServerData(data);
};

export const deleteDirectoryConfiguration = async (id: string): Promise<DirectoryConfigurationModel> => {
  const { data } = await apiReq("DELETE", `/v1/directoryConfigurations/${id}`);

  return DirectoryConfigurationModel.fromServerData(data);
};

export type TInitDirectoryBody = {
  attributesChanges?: TAttributesChangesMap;
  excludedGroups?: string[];
  includedGroups?: string[];
  syncMode: DirectorySyncMode;
};

export async function checkConfig(
  directory: TDirectory,
  configuration: Record<string, unknown>,
  agentToken?: null | string,
) {
  const { data } = await apiReq("POST", "/v1/directoryConfigurations/checkConfig", {
    agentToken,
    configuration,
    directory,
  });
  const checkConfigResult = data as ICheckConfig;
  return { isValid: checkConfigResult.isValidConfig, message: checkConfigResult.message };
}

export async function getDirectoryConfigurationStatus(id: string): Promise<DirectoryConfigurationStatus> {
  const { data } = await apiReq<{ status: DirectoryConfigurationStatus }>(
    "GET",
    `/v1/directoryConfigurations/${id}/status`,
  );
  return data.status;
}

export async function getGroupsCount(
  id: string,
  options: { withInUseCount?: boolean } = {},
): Promise<{ inUseGroups?: number; selectedGroups: number; totalGroups: number }> {
  const { data } = await apiReq<{ selectedGroups: number; totalGroups: number }>(
    "GET",
    `/v1/directoryConfigurations/${id}/groupsCount?${getAsQueryParams(options)}`,
  );
  return data;
}

export async function initDirectory(id: string, body: TInitDirectoryBody): Promise<void> {
  await apiReq("POST", `/v1/directoryConfigurations/${id}/init`, body);
}

export async function syncDirectory(id: string) {
  const { data } = await apiReq("POST", `/v1/directoryConfigurations/${id}/sync`);
  return DirectoryConfigurationModel.fromServerData(data);
}

export async function syncDirectoryPreview(id: string): Promise<void> {
  await apiReq("POST", `/v1/directoryConfigurations/${id}/syncDirectoryPreview`);
}

export const adjustConfiguration = (obj: object, toSnakeCase: boolean): Record<string, unknown> => {
  return Object.entries(obj)
    .reduce((acc, [key, value]) => {
      const fixedKey = toSnakeCase ? transformToSnakeCase(key) : key;
      return acc.set(
        fixedKey,
        isObject(value)
          ? adjustConfiguration(value, toSnakeCase)
          : typeof value === "string"
            ? removeRedundantSpaces(value)
            : value,
      );
    }, Map<string, unknown>())
    .toObject();
};

const transformToSnakeCase = (val: string) => {
  const reg = new RegExp(/([A-Z])/g);
  return val.replace(reg, (v) => `_${v.toLowerCase()}`);
};

export const fromSnakeToCamel = (data: object): object => {
  if (isArray(data)) {
    return map(data, fromSnakeToCamel);
  }

  if (isObject(data)) {
    return chain(data)
      .mapKeys((v, k) => camelCase(k))
      .mapValues((v, k) => fromSnakeToCamel(v))
      .value();
  }

  return data;
};
